/**
 * Distributed Jobs Action Definitions
 *
 * Long-running computational jobs submitted to the Temporal-based job
 * orchestrator (Jobman). These run asynchronously on GPU or CPU worker nodes.
 *
 * @module gibbs/registry/actions/distributed-jobs
 */

import { WorkflowTypes as T } from '../types';
import { param, pre } from '../schema';

// ─── Protein Folding Job ────────────────────────────────────────────────────

export const jobFoldProtein = {
    id: 'job-fold-protein',
    name: 'Protein Folding Job',
    category: 'Distributed Jobs',
    description: 'Submit protein structure prediction to the job manager. Runs AI folding algorithms (Boltz-1, Boltz-2, Chai-1) on GPU workers. Results are loaded as a new protein structure in the workspace.',
    inputs: [
        param('foldingProgramId', 'string', 'Folding algorithm: boltz-1 | boltz-2 | chai-1'),
        param('foldingArgs', 'object', 'Algorithm-specific parameters (sequence, ligands, etc.)'),
    ],
    outputs: [
        param('jobId', T.JobId, 'Tracking reference'),
        param('proteinStructure', T.ProteinStructure, 'Predicted structure (on completion)'),
        param('resultProperties', T.PropertySet, 'Confidence metrics (pLDDT, pTM, ipTM)'),
    ],
    parameters: [
        param('foldingProgramId', 'string', 'Algorithm to use', true, { options: ['boltz-1', 'boltz-2', 'chai-1'] }),
    ],
    dependsOn: [],
    preconditions: [
        pre('jobman_available', 'Job manager service must be available'),
    ],
    postconditions: [],
    runtime: 'background',
    status: 'implemented',
    userAction: null,
    serverCommand: null,
    jobType: 'folding',
};

// ─── GNINA Scoring Job ──────────────────────────────────────────────────────

export const jobGninaScoring = {
    id: 'job-gnina-scoring',
    name: 'GNINA Scoring Job',
    category: 'Distributed Jobs',
    description: 'Score compound binding poses using GNINA\'s CNN-based scoring function. Fast evaluation without generating new poses. Returns affinity, cnn_score, cnn_affinity, cnn_vs, and cnn_variance per compound.',
    inputs: [
        param('receptorPdb', T.PDB, 'Protein structure'),
        param('ligandSdf', T.SDF, 'Compound(s) to score'),
    ],
    outputs: [
        param('jobId', T.JobId, 'Tracking reference'),
        param('scores', T.PropertySet, 'Per-compound scores: {affinity, cnn_score, cnn_affinity, cnn_vs, cnn_variance} (on completion)'),
    ],
    parameters: [
        param('scoring', 'string', 'Scoring function variant', false),
        param('cnn_scoring', 'string', 'CNN scoring variant', false),
        param('cnn', 'string', 'CNN model', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
        pre('compound_loaded', 'Compounds must be loaded'),
        pre('jobman_available', 'Job manager service must be available'),
    ],
    postconditions: [],
    runtime: 'background',
    status: 'implemented',
    userAction: null,
    serverCommand: null,
    jobType: 'gnina_scoring',
};

// ─── GNINA Docking Job ──────────────────────────────────────────────────────

export const jobGninaDocking = {
    id: 'job-gnina-docking',
    name: 'GNINA Docking Job',
    category: 'Distributed Jobs',
    description: 'Full molecular docking using GNINA with CNN-refined scoring. Generates binding poses and scores. Supports whole-protein or box-constrained docking. Returns per-pose affinity (kcal/mol), cnn_affinity (pK), cnn_score (probability), cnn_vs, and cnn_variance.',
    inputs: [
        param('receptorPdb', T.PDB, 'Protein structure'),
        param('ligandSdf', T.SDF, 'Compound(s) to dock'),
        param('boxParams', T.DockingBox, 'Docking search box constraints', false),
    ],
    outputs: [
        param('jobId', T.JobId, 'Tracking reference'),
        param('dockedPoses', T.CompoundList, 'Docked poses as SDF with properties (on completion)'),
        param('scores', T.PropertySet, 'Per-pose: {affinity, cnn_affinity, cnn_score, cnn_vs, cnn_variance}'),
    ],
    parameters: [
        param('speedArg', 'string', 'Speed preset (maps to exhaustiveness: accurate=64-128, fast=8, faster=4-8)', false, { options: ['accurate', 'fast', 'faster'] }),
        param('poseCount', 'number', 'Number of poses (num_modes)', false),
        param('boxParams', T.DockingBox, 'Search space definition', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
        pre('compound_loaded', 'Compounds must be loaded'),
        pre('jobman_available', 'Job manager service must be available'),
    ],
    postconditions: [],
    runtime: 'background',
    status: 'implemented',
    userAction: 'DockWithJobManager',
    serverCommand: null,
    jobType: 'gnina_docking',
};

// ─── GNINA Flexible Docking Job ─────────────────────────────────────────────

export const jobGninaFlexibleDocking = {
    id: 'job-gnina-flexible-docking',
    name: 'GNINA Flexible Docking Job',
    category: 'Distributed Jobs',
    description: 'Molecular docking using GNINA with flexible protein side chains. Similar to standard GNINA docking but allows specified protein residues to move during the docking process, improving pose accuracy for induced-fit binding.',
    inputs: [
        param('receptorPdb', T.PDB, 'Protein structure'),
        param('ligandSdf', T.SDF, 'Compound(s) to dock'),
        param('flexibleResidues', 'object', 'Residue specifications for flexible side chains'),
        param('boxParams', T.DockingBox, 'Docking search box constraints', false),
    ],
    outputs: [
        param('jobId', T.JobId, 'Tracking reference'),
        param('dockedPoses', T.CompoundList, 'Docked poses with flexible protein conformations (on completion)'),
        param('scores', T.PropertySet, 'Per-pose: {affinity, cnn_affinity, cnn_score, cnn_vs, cnn_variance}'),
    ],
    parameters: [
        param('speedArg', 'string', 'Speed preset', false, { options: ['accurate', 'fast', 'faster'] }),
        param('poseCount', 'number', 'Number of poses (num_modes)', false),
        param('boxParams', T.DockingBox, 'Search space definition', false),
        param('flexibleResidues', 'object', 'Which protein residues to allow movement', true),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
        pre('compound_loaded', 'Compounds must be loaded'),
        pre('binding_site_defined', 'Binding site residues must be identified'),
        pre('jobman_available', 'Job manager service must be available'),
    ],
    postconditions: [],
    runtime: 'background',
    status: 'implemented',
    userAction: null,
    serverCommand: null,
    jobType: 'gnina_flexible_docking',
};

// ─── REINVENT Generative Design Job ─────────────────────────────────────────

export const jobReinvent = {
    id: 'job-reinvent',
    name: 'REINVENT Generative Design Job',
    category: 'Distributed Jobs',
    description: 'AI-driven de novo molecular generation using REINVENT 4. Generates novel compounds optimized for multiple objectives (drug-likeness, synthetic accessibility, target properties) via reinforcement learning. Result properties per molecule include: Score (0-1, composite optimization), Agent/Prior/Target (RL likelihoods), Molecular weight (Da, goodRange 200-500), HB acceptors (Lipinski, goodRange <=10), HB donors (Lipinski, goodRange <=5), QED Score (0-1, goodRange >0.6), SA score (1-10, goodRange <6), SlogP (goodRange 1-3), TPSA (A^2, goodRange 20-130), plus 14+ additional RDKit descriptors (NumRotBond, NumAromaticRings, etc.).',
    inputs: [
        param('jobName', 'string', 'User-supplied job name'),
        param('smilesList', 'object', 'Starting molecule(s) as SMILES array'),
        param('objectives', 'object', 'Multi-objective optimization targets'),
    ],
    outputs: [
        param('jobId', T.JobId, 'Tracking reference'),
        param('generatedMolecules', T.CSV, 'Table of generated molecules with scores (on completion)'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('jobman_available', 'Job manager service must be available'),
    ],
    postconditions: [],
    runtime: 'background',
    status: 'implemented',
    userAction: null,
    serverCommand: null,
    jobType: 'reinvent',
};

// ─── MapLight ADMET Prediction Job ──────────────────────────────────────────

export const jobMaplight = {
    id: 'job-maplight',
    name: 'MapLight ADMET Prediction Job',
    category: 'Distributed Jobs',
    description: 'Predict the 22 canonical ADMET (Absorption, Distribution, Metabolism, Excretion, Toxicity) properties currently shipped by the MapLight ML model. Results are applied as scoped properties on the compound objects.',
    inputs: [
        param('compoundDict', 'object', 'Compounds by unique structure ID: {cmpdId: {smiles: SMILES}, ...}'),
    ],
    outputs: [
        param('jobId', T.JobId, 'Tracking reference'),
        param('predictions', T.PropertySet, 'Per-compound ADMET properties (on completion)'),
    ],
    parameters: [
        param('recalculate', 'boolean', 'Force recalculation even if cached', false, { defaultValue: false }),
    ],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'Compounds must be loaded with SMILES'),
        pre('jobman_available', 'Job manager service must be available'),
    ],
    postconditions: [],
    runtime: 'background',
    status: 'implemented',
    userAction: null,
    serverCommand: null,
    jobType: 'maplight',
};

// ─── Library Screening Job ──────────────────────────────────────────────────

export const jobLibraryScreen = {
    id: 'job-library-screen',
    name: 'Library Screening Job',
    category: 'Distributed Jobs',
    description: 'Batch screening of compounds against protein targets using a multi-step Temporal workflow. Supports multiple scoring functions (GNINA, AutoDock, Boltz affinity). Processes compounds in parallel batches with a rollup aggregation step. Result properties (65+ metrics) include: Boltz-2 Affinity (affinity_pred_value as log IC50 uM, affinity_probability_binary, derived IC50 in uM and nM), Boltz-2 Confidence (confidence_score, ptm, iptm, ligand_iptm, protein_iptm, complex_plddt, complex_iplddt, complex_pde, complex_ipde), BFD Energy (vdw, electrostatics, interaction_total, stress, ddGs, amber_total, energy_sum in kcal/mol), GNINA (minimizedAffinity, CNNaffinity, CNNscore, CNN_VS, CNNaffinity_variance).',
    inputs: [
        param('jobName', 'string', 'User-supplied job name'),
        param('compounds', T.CompoundList, 'Compounds to screen'),
        param('targets', 'object', 'Target proteins (ProteinStructure[])'),
        param('screenSpec', 'object', 'Screening configuration'),
    ],
    outputs: [
        param('jobId', T.JobId, 'Tracking reference'),
        param('results', 'object', 'Per-compound aggregated results across targets (on completion)'),
    ],
    parameters: [
        param('scoringFunctions', 'object', 'Scoring function configuration', false),
        param('batchSize', 'number', 'Concurrent batch size', false, { defaultValue: 5 }),
        param('librarySpec', 'object', 'Library specification', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'Compounds must be loaded'),
        pre('protein_loaded', 'Protein targets must be loaded'),
        pre('jobman_available', 'Job manager service must be available'),
    ],
    postconditions: [],
    runtime: 'background',
    status: 'implemented',
    userAction: null,
    serverCommand: null,
    jobType: 'library_screen',
};

// ─── Submit Job ─────────────────────────────────────────────────────────────

export const jobSubmit = {
    id: 'job-submit',
    name: 'Submit Job',
    category: 'Distributed Jobs',
    description: 'Submit any job type to the Temporal-based job orchestrator.',
    inputs: [
        param('jobtype', 'string', 'Job type identifier'),
        param('jobrequest', 'object', 'Job-specific parameters'),
        param('stash', 'object', 'Context data passed through to results', false),
    ],
    outputs: [
        param('jobId', T.JobId, 'Assigned job identifier'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('jobman_available', 'Job manager service must be available'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'JobSubmit',
    serverCommand: null,
    jobType: null,
};

// ─── Monitor Job Status ─────────────────────────────────────────────────────

export const jobMonitor = {
    id: 'job-monitor',
    name: 'Monitor Job Status',
    category: 'Distributed Jobs',
    description: 'Check the status of submitted jobs. Returns state (RUNNING, SUCCEEDED, FAILED) and progress information.',
    inputs: [],
    outputs: [
        param('jobs', 'object', 'List of jobs with state, metadata, and progress'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('jobman_available', 'Job manager service must be available'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'JobMonitor',
    serverCommand: null,
    jobType: null,
};

// ─── Retrieve Job Results ───────────────────────────────────────────────────

export const jobRetrieveResults = {
    id: 'job-retrieve-results',
    name: 'Retrieve Job Results',
    category: 'Distributed Jobs',
    description: 'Fetch completed job results and integrate them into the workspace. Output varies by job type (see individual job definitions).',
    inputs: [
        param('jobId', T.JobId, 'Job to retrieve'),
    ],
    outputs: [
        param('results', 'object', 'Job-specific results (varies by job type)'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('jobman_available', 'Job manager service must be available'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'JobRetrieveResults',
    serverCommand: null,
    jobType: null,
};

// ─── Default Export ─────────────────────────────────────────────────────────

export default [
    jobFoldProtein,
    jobGninaScoring,
    jobGninaDocking,
    jobGninaFlexibleDocking,
    jobReinvent,
    jobMaplight,
    jobLibraryScreen,
    jobSubmit,
    jobMonitor,
    jobRetrieveResults,
];
